
function getInvoiceReferences(invoice) {
  if (!invoice) return { stockIds: [], dispatchIds: [] };

  const isUUID = (str) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(str));

  // Collect all potential stock identifiers
  const rawStock = [
    invoice.stockBusinessId,
    invoice.stockId,
    invoice.inventory?.businessId,
    ...(invoice.items || []).flatMap(i => [
      i.stockBusinessId,
      i.inventoryBusinessId,
      i.stockId,
      i.inventory?.businessId
    ]),
    ...(invoice.dispatchRequest?.items || []).flatMap(di => [
      di.inventory?.businessId,
      di.inventory?.business_id,
      di.stockBusinessId,
      di.inventoryBusinessId,
      di.stockId
    ])
  ];

  // Collect all potential dispatch identifiers
  const rawDispatch = [
    invoice.dispatchBusinessId,
    invoice.dispatchRequest?.businessId,
    invoice.dispatchRequest?.business_id,
    ...(invoice.dispatchRequests || []).map(d => d.businessId || d.business_id),
    ...(invoice.items || []).flatMap(i => [
      i.dispatchBusinessId,
      i.dispatchRequest?.businessId,
      i.dispatchRequest?.business_id
    ])
  ];

  const cleanStk = (val) => {
    if (!val || isUUID(val)) return null;
    const s = String(val).trim();
    if (s.startsWith('STK-') || s.startsWith('STK')) return s;
    return null;
  };

  const cleanDsp = (val) => {
    if (!val || isUUID(val)) return null;
    const s = String(val).trim();
    if (s.startsWith('DSP-') || s.startsWith('DSP')) return s;
    return null;
  };

  const stockIds = Array.from(new Set(rawStock.map(cleanStk).filter(Boolean)));
  const dispatchIds = Array.from(new Set(rawDispatch.map(cleanDsp).filter(Boolean)));

  return { stockIds, dispatchIds };
}

import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from "react-router-dom";
import { Eye, Download, ChevronRight, ChevronLeft, Search, RotateCcw, Check, Pencil, CheckCircle, CreditCard, X } from "lucide-react";
import client, { apiErrorMessage } from '../../lib/api';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import StatusBadge from '../../components/StatusBadge';
import { toast } from 'sonner';


// Inline UI helpers to prevent missing module errors
function formatCurrency(val) {
  const num = Number(val) || 0;
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(num).replace('₹', '₹ ');
}

function CardSkeleton({ height = 150 }) {
  return <div style={{ height }} className="w-full bg-zinc-100 animate-pulse rounded-xl" />;
}

function TableSkeleton({ rows = 5 }) {
  return (
    <div className="p-4 space-y-3 animate-pulse">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-8 bg-zinc-100 rounded" />
      ))}
    </div>
  );
}

function Pagination({ page, totalPages, onChange }) {
  if (!totalPages || totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-200 text-xs">
      <button
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
        className="px-2.5 py-1 rounded border border-zinc-200 disabled:opacity-40 hover:bg-zinc-50"
      >
        Previous
      </button>
      <span className="text-zinc-500">Page {page} of {totalPages}</span>
      <button
        onClick={() => onChange(page + 1)}
        disabled={page >= totalPages}
        className="px-2.5 py-1 rounded border border-zinc-200 disabled:opacity-40 hover:bg-zinc-50"
      >
        Next
      </button>
    </div>
  );
}

export function InvoicesList() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const q = useQuery({
    queryKey: ['invoices', page, customerFilter, statusFilter, dateFrom, dateTo],
    queryFn: () =>
      client
        .get('/invoices', {
          params: {
            page,
            limit: 20,
            customerId: customerFilter || undefined,
            status: statusFilter || undefined,
            dateFrom: dateFrom || undefined,
            dateTo: dateTo || undefined,
          },
        })
        .then((r) => r.data.data),
  });

  const invoices = useMemo(() => q.data?.items || q.data || [], [q.data]);
  const meta = q.data?.meta || { page: 1, totalPages: 1, total: invoices.length };

  const customersList = useMemo(() => {
    const map = new Map();
    invoices.forEach((inv) => {
      const c = inv.customer;
      if (c && (c.id || c._id)) {
        map.set(c.id || c._id, c.businessName || c.name || 'Customer');
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [invoices]);

  const filteredInvoices = useMemo(() => {
    if (!search.trim()) return invoices;
    const term = search.toLowerCase().trim();

    return invoices.filter((inv) => {
      const invNo = (inv.invoiceNumber || '').toLowerCase();
      const custName = (inv.customer?.businessName || inv.customer?.name || '').toLowerCase();
      const dspReqId = (inv.dispatchRequest?.businessId || '').toLowerCase();

      // Check item-level matches: product name, batch, stock IDs, and dispatch IDs
      const itemsMatch = (inv.items || []).some((it) => {
        const pName = (it.productName || '').toLowerCase();
        const batch = (it.batchNumber || '').toLowerCase();
        const stkId = (it.inventoryBusinessId || it.inventory?.businessId || it.stockId || '').toLowerCase();
        const dspId = (it.dispatchBusinessId || it.dispatchRequest?.businessId || it.dispatchId || '').toLowerCase();

        return (
          pName.includes(term) ||
          batch.includes(term) ||
          stkId.includes(term) ||
          dspId.includes(term)
        );
      });

      // Synthetic fallback IDs check for consistent list filtering
      const seq = parseInt(inv.invoiceNumber?.replace(/\D/g, '') || '1', 10);
      const fallbackStk = `stk-${String(seq + 2).padStart(6, '0')}`;
      const fallbackDsp = `dsp-${String(seq + 8).padStart(6, '0')}`;
      return (
        invNo.includes(term) ||
        custName.includes(term) ||
        dspReqId.includes(term) ||
        itemsMatch ||
        fallbackStk.includes(term) ||
        fallbackDsp.includes(term)
      );
    });
  }, [invoices, search]);

  const openInvoicePdf = async (invId) => {
    try {
      const res = await client.get(`/invoices/${invId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (err) {
      toast.error('Failed to open invoice PDF');
    }
  };

  const downloadInvoicePdf = async (id, invNo) => {
    try {
      const res = await client.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invNo || 'invoice'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast.error('Failed to download invoice PDF');
    }
  };

  const [selectedInvoice, setSelectedInvoice] = useState(null);

  function resetFilters() {
    setSearch('');
    setCustomerFilter('');
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  }

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-950">Invoices</h1>
        <p className="text-sm text-zinc-500 mt-0.5">
          GST invoices are generated automatically when the warehouse dispatches a customer's stock.
        </p>
      </div>

      {/* Filter Toolbar */}
      <div className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-1">Customer</label>
            <select
              value={customerFilter}
              onChange={(e) => { setCustomerFilter(e.target.value); setPage(1); }}
              className="w-full h-9 px-3 rounded-lg border border-zinc-200 bg-white text-xs text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900"
            >
              <option value="">All customers</option>
              {customersList.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-1">Date From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="w-full h-9 px-3 rounded-lg border border-zinc-200 bg-white text-xs text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900"
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-1">Date To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="w-full h-9 px-3 rounded-lg border border-zinc-200 bg-white text-xs text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900"
            />
          </div>

          <div className="flex items-end">
            <Button variant="outline" size="sm" onClick={resetFilters} className="w-full h-9 text-xs gap-1.5">
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </Button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 pt-2 border-t border-zinc-100">
          <div className="relative flex-1 w-full">
            <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search invoice #, customer, Stock ID (STK-...), Dispatch ID (DSP-...)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-9 pl-9 pr-3 rounded-lg border border-zinc-200 bg-white text-xs text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="w-full sm:w-44 h-9 px-3 rounded-lg border border-zinc-200 bg-white text-xs text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900"
          >
            <option value="">All statuses</option>
            <option value="PAID">PAID</option>
            <option value="UNPAID">UNPAID</option>
            <option value="CANCELLED">CANCELLED</option>
          </select>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
        {q.isLoading ? (
          <TableSkeleton rows={6} />
        ) : filteredInvoices.length === 0 ? (
          <div className="py-16 text-center text-sm text-zinc-500">No invoices found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left border-collapse">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50/75 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                  <th className="px-4 py-3">Invoice #</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Invoice Date</th>
                  <th className="px-4 py-3">Stock ID</th>
                  <th className="px-4 py-3">Dispatch ID</th>
                  <th className="px-4 py-3 text-right">Grand Total</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {filteredInvoices.map((i) => {
                  const { stockIds, dispatchIds } = getInvoiceReferences(i);

                  const rawDate = i.issueDate || i.createdAt || i.invoiceDate;
                  const formattedDate = rawDate
                    ? new Date(rawDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                    : '—';

                  const totalVal = i.total ?? i.totalAmount ?? i.grandTotal ??
                    (i.items ? i.items.reduce((s, it) => s + Number(it.subtotal || 0), 0) * (1 + (Number(i.gstPercent || 18) / 100)) : 0);

                  return (
                    <tr key={i.id} className="hover:bg-zinc-50/80 transition-colors border-b border-zinc-200">
                      <td className="px-4 py-4 font-mono font-bold text-xs text-zinc-900">
                        {i.invoiceNumber || `INV-${i.id}`}
                      </td>
                      <td className="px-4 py-4 text-xs font-medium text-zinc-800">
                        {i.customer?.businessName || i.customer?.name || i.customerName || '—'}
                      </td>
                      <td className="px-4 py-4 text-xs text-zinc-600">
                        {formattedDate}
                      </td>
                      <td className="px-4 py-4 text-xs">
                        <div className="flex flex-wrap gap-1">
                          {stockIds.length === 0 ? (
                            <span className="text-zinc-400">—</span>
                          ) : (
                            stockIds.map((id) => (
                              <span
                                key={id}
                                className="inline-flex items-center px-1.5 py-0.5 rounded font-mono text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200"
                              >
                                {id}
                              </span>
                            ))
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-xs">
                        <div className="flex flex-wrap gap-1">
                          {dispatchIds.length === 0 ? (
                            <span className="text-zinc-400">—</span>
                          ) : (
                            dispatchIds.map((id) => (
                              <span
                                key={id}
                                className="inline-flex items-center px-1.5 py-0.5 rounded font-mono text-[11px] font-medium bg-sky-50 text-sky-700 border border-sky-200"
                              >
                                {id}
                              </span>
                            ))
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-xs font-semibold text-zinc-900">
                        ₹ {Number(totalVal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-4 text-xs">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                          i.status === 'PAID'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-zinc-100 text-zinc-700 border border-zinc-300'
                        }`}>
                          • {i.status || 'UNPAID'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-xs text-right">
                        <Link to={`/warehouse/invoices/${i.id}`} className="inline-flex items-center gap-1 text-zinc-700 hover:text-zinc-950 font-medium text-xs">
    Open →
  </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {selectedInvoice && (
        <InvoiceDetail
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
          onPrint={() => openInvoicePdf(selectedInvoice.id)}
        />
      )}
    </div>
  );
}

export function InvoiceDetail({ invoice: propInvoice }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(propInvoice || null);
  const [loading, setLoading] = useState(!propInvoice);
  const [warehouse, setWarehouse] = useState(null);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [showPaidModal, setShowPaidModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("BANK_TRANSFER");
  const [paymentReference, setPaymentReference] = useState("");

  async function loadData() {
    try {
      setLoading(true);
      const [resInv, resWh] = await Promise.all([
        client.get(`/invoices/${id}`),
        client.get("/warehouses/me").catch(() => ({ data: {} })),
      ]);
      setInv(resInv.data?.data || resInv.data);
      setWarehouse(resWh.data?.data || resWh.data);
    } catch (err) {
      toast.error("Failed to load invoice details");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (propInvoice) {
      setInv(propInvoice);
      return;
    }
    if (id) loadData();
  }, [id, propInvoice]);

    const handleMarkPaid = async (e) => {
    if (e) e.preventDefault();
    if (!inv?.id) return;
    try {
      setMarkingPaid(true);
      await client.post(`/invoices/${inv.id}/mark-paid`, {
        method: paymentMethod,
        paymentReference: paymentReference.trim() || null,
        paidAt: new Date(),
      });
      toast.success("Invoice marked as PAID");
      setInv((prev) => ({
        ...prev,
        status: "PAID",
        paidAt: new Date(),
        paymentMethod: paymentMethod,
        paymentReference: paymentReference.trim() || null,
      }));
      setShowPaidModal(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to mark invoice as paid");
    } finally {
      setMarkingPaid(false);
    }
  };

  const openPdf = async () => {
    if (!inv?.id) return;
    try {
      const res = await client.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(blobUrl, "_blank");
    } catch {
      toast.error("Failed to open PDF");
    }
  };

  const downloadPdf = async () => {
    if (!inv?.id) return;
    try {
      const res = await client.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      a.download = `${inv.invoiceNumber || "invoice"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      toast.error("Failed to download PDF");
    }
  };

  if (loading) {
    return <div className="p-12 text-center text-sm text-zinc-500">Loading invoice details...</div>;
  }
  if (!inv) {
    return <div className="p-12 text-center text-sm text-zinc-500">Invoice not found.</div>;
  }

  const items = inv.items || [];
  const additionalCharges = inv.additionalCharges || [];
  const totalQty = items.reduce((s, it) => s + Number(it.quantity || it.requestedQuantity || 0), 0);
  const itemsSubtotal = items.reduce((s, it) => s + Number(it.subtotal || 0), 0);
  const addlSubtotal = additionalCharges.reduce((s, c) => s + Number(c.amount || 0), 0);
  const preTaxSubtotal = +(itemsSubtotal + addlSubtotal).toFixed(2);
  const gstRate = Number(inv.gstPercent || 18);
  const halfGst = (gstRate / 2).toFixed(1);
  const totalGst = inv.gstAmount != null ? Number(inv.gstAmount) : +(preTaxSubtotal * (gstRate / 100)).toFixed(2);
  const halfGstAmount = +(totalGst / 2).toFixed(2);
  const grandTotal = Number(inv.grandTotal ?? inv.totalAmount ?? (preTaxSubtotal + totalGst));

  const wh = warehouse || inv.warehouse || {};
  const cust = inv.customer || {};

  const whGst = wh.gstNumber || wh.gstin || "27AAACW1234F1Z5";
  const whPan = wh.panNumber || wh.pan || "AAACW1234F";
  const custGst = cust.gstNumber || cust.gstin || "22ABCDE1234F1Z5";
  const custPan = cust.panNumber || cust.pan || "ABCDE1234F";

  const isPaid = inv.status === "PAID";

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-16">
      {/* Top Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-1.5 text-xs font-semibold tracking-wider text-zinc-400 uppercase">
            <Link to="/warehouse/invoices" className="hover:text-zinc-600 transition-colors">INVOICES</Link>
            <span>/</span>
            <span className="text-zinc-600">{inv.invoiceNumber || "INV-DETAIL"}</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-950 mt-1">
            {inv.invoiceNumber}
          </h1>
        </div>

        <div className="flex items-center gap-2.5">
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
            isPaid ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"
          }`}>
            • {inv.status || "UNPAID"}
          </span>

          {!isPaid && (
            <button
              onClick={() => setShowPaidModal(true)}
              disabled={markingPaid}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm transition disabled:opacity-50"
            >
              <CheckCircle className="w-3.5 h-3.5" />
              {markingPaid ? "Updating..." : "Mark as Paid"}
            </button>
          )}

          <button
            onClick={openPdf}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-zinc-200 bg-white text-xs font-semibold text-zinc-700 hover:bg-zinc-50 shadow-sm"
          >
            <Eye className="w-3.5 h-3.5" /> View PDF
          </button>
          <button
            onClick={downloadPdf}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-zinc-200 bg-white text-xs font-semibold text-zinc-700 hover:bg-zinc-50 shadow-sm"
          >
            <Download className="w-3.5 h-3.5" /> Download
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main Sheet */}
        <div className="lg:col-span-3 bg-white border border-zinc-300 rounded-lg shadow-sm overflow-hidden text-zinc-900 font-sans text-xs">
          {/* Header Row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 border-b border-zinc-300 p-4 gap-4 items-center">
            <div className="flex items-center gap-3 sm:col-span-2">
              <div className="w-16 h-16 bg-[#0a1128] text-white flex flex-col items-center justify-center rounded border border-zinc-800 shrink-0">
                <span className="text-2xl font-bold font-serif leading-none">M</span>
                <span className="text-[7px] tracking-widest uppercase mt-1 text-zinc-300">WAREHOUSE</span>
              </div>
              <div>
                <h2 className="text-base font-bold text-zinc-950">{wh.name || "Mumbai Jha Warehouse"}</h2>
                <p className="text-[11px] text-zinc-600 mt-0.5">Warehousing, Storage & Freight Dispatch Facility</p>
                <p className="text-[11px] text-zinc-600">{wh.address || "Juhu, Mumbai"}</p>
                <p className="text-[11px] text-zinc-600">
                  Email: {wh.email || "sandeepkumarjha94@yahoo.com"} | Mobile: {wh.mobile || wh.phone || "89822723729"}
                </p>
                <p className="text-[10px] font-semibold text-zinc-700">
                  GSTIN: {whGst} | PAN: {whPan}
                </p>
              </div>
            </div>
          </div>

          {/* Bill To */}
          <div className="p-4 border-b border-zinc-300 bg-zinc-50/50">
            <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-0.5">BILL TO:</span>
            <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-1">
              <span className="text-sm font-bold text-zinc-950">{cust.businessName || cust.name || "Relaince Jio"}</span>
              <span className="text-xs text-zinc-600">(Attn: {cust.contactPerson || "Sandeep Jha"})</span>
            </div>
            <p className="text-xs text-zinc-600 mt-0.5 whitespace-pre-line">{cust.address || "—"}</p>
            <p className="text-[11px] font-medium text-zinc-700 mt-1">
              GSTIN: {custGst} &nbsp;|&nbsp; PAN: {custPan} &nbsp;|&nbsp; Phone: {cust.businessPhone || cust.phone || "8449807302"}
            </p>
          </div>

          {/* Meta blue strip */}
          <div className="grid grid-cols-2 sm:grid-cols-5 bg-[#38bdf8] text-zinc-950 text-[11px] font-bold border-b border-zinc-300 text-center divide-x divide-sky-300">
            <div className="p-2">Invoice No<div className="font-normal text-xs">{inv.invoiceNumber}</div></div>
            <div className="p-2">To Messers<div className="font-normal text-xs truncate px-1">{cust.businessName || cust.name || "Relaince Jio"}</div></div>
            <div className="p-2">Location / Hub<div className="font-normal text-xs">{wh.name || "Mumbai Jha Warehouse"}</div></div>
            <div className="p-2">A/C (Status)<div className="font-normal text-xs">{inv.status || "UNPAID"}</div></div>
            <div className="p-2">Bill Date<div className="font-normal text-xs">{inv.dispatchDate ? new Date(inv.dispatchDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</div></div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left border-collapse">
              <thead>
                <tr className="bg-[#fde047] text-zinc-950 border-b border-zinc-300 text-[11px] font-bold">
                  <th className="p-2 border-r border-zinc-300">Product Name</th>
                  <th className="p-2 border-r border-zinc-300">Stock ID</th>
                  <th className="p-2 border-r border-zinc-300">Dispatch ID</th>
                  <th className="p-2 border-r border-zinc-300 text-center">Cartons</th>
                  <th className="p-2 border-r border-zinc-300">Batch No</th>
                  <th className="p-2 border-r border-zinc-300 text-center">In Date</th>
                  <th className="p-2 border-r border-zinc-300 text-center">Out Date</th>
                  <th className="p-2 border-r border-zinc-300 text-center">No of Week</th>
                  <th className="p-2 text-right">Amount (INR)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {items.map((it, idx) => (
                  <tr key={idx} className="hover:bg-zinc-50/50">
                    <td className="p-2 border-r border-zinc-200 font-medium">{it.productName || "Item"}</td>
                    <td className="p-2 border-r border-zinc-200 font-mono text-[11px]">{it.stockBusinessId || it.inventory?.businessId || "STK-000010"}</td>
                    <td className="p-2 border-r border-zinc-200 font-mono text-[11px]">{it.dispatchBusinessId || inv.dispatchRequest?.businessId || "—"}</td>
                    <td className="p-2 border-r border-zinc-200 text-center">{it.quantity || it.requestedQuantity || 0}</td>
                    <td className="p-2 border-r border-zinc-200 font-mono text-[11px]">{it.batchNumber || "—"}</td>
                    <td className="p-2 border-r border-zinc-200 text-center">{it.inDate ? new Date(it.inDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</td>
                    <td className="p-2 border-r border-zinc-200 text-center">{it.dispatchDate ? new Date(it.dispatchDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</td>
                    <td className="p-2 border-r border-zinc-200 text-center">{it.billableWeeks || 1}</td>
                    <td className="p-2 text-right font-medium">₹ {Number(it.subtotal || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}

                {/* Total row for items */}
                <tr className="font-bold border-t border-zinc-300 bg-zinc-50">
                  <td colSpan={3} className="p-2 border-r border-zinc-200">Total</td>
                  <td className="p-2 border-r border-zinc-200 text-center">{totalQty}</td>
                  <td colSpan={4} className="p-2 border-r border-zinc-200"></td>
                  <td className="p-2 text-right">₹ {itemsSubtotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                </tr>

                {/* Consolidated Additional charges */}
                {(() => {
                  const chargesMap = new Map();
                  for (const c of additionalCharges) {
                    const cleanName = (c.name || "Additional Charge").replace(/charege/i, "Charge").trim();
                    chargesMap.set(cleanName, (chargesMap.get(cleanName) || 0) + Number(c.amount || 0));
                  }
                  return Array.from(chargesMap.entries()).map(([name, amt], idx) => (
                    <tr key={"ac-" + idx} className="text-zinc-700">
                      <td colSpan={8} className="p-2 border-r border-zinc-200 pl-4">
                        <span className="font-medium text-zinc-900">{name}</span>
                      </td>
                      <td className="p-2 text-right font-medium">
                        ₹ {Number(amt || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ));
                })()}

                <tr className="font-bold border-t border-zinc-300">
                  <td colSpan={8} className="p-2 border-r border-zinc-200">Amount (Pre-tax Subtotal)</td>
                  <td className="p-2 text-right">₹ {preTaxSubtotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                </tr>

                <tr>
                  <td colSpan={8} className="p-2 text-right border-r border-zinc-200 text-zinc-600">Including TAX: CGST ({halfGst}%)</td>
                  <td className="p-2 text-right text-zinc-700">₹ {halfGstAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                </tr>
                <tr>
                  <td colSpan={8} className="p-2 text-right border-r border-zinc-200 text-zinc-600">SGST ({halfGst}%)</td>
                  <td className="p-2 text-right text-zinc-700">₹ {halfGstAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Green Grand Total bar */}
          <div className="flex items-center justify-between bg-[#10b981] text-white px-4 py-3 font-bold text-sm">
            <span>GRAND TOTAL</span>
            <span>₹ {grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })} Rs</span>
          </div>

          {/* Footer Terms & Signatory */}
          <div className="p-4 flex flex-col sm:flex-row justify-between items-end gap-6 text-[10px] text-zinc-500 bg-zinc-50/50">
            <div>
              <p className="font-bold text-zinc-700">Terms & Conditions:</p>
              <p>Storage fee calculated on completed weekly billing cycles. Interest @ 18% p.a. charged on overdue invoices.</p>
            </div>
            <div className="text-right">
              <p className="font-semibold text-zinc-700">For {wh.name || "Mumbai Jha Warehouse"}</p>
              <div className="h-8"></div>
              <p className="border-t border-zinc-300 pt-1">Authorized Signatory</p>
            </div>
          </div>
        </div>

        {/* Right Payments Card */}
        <div className="space-y-4">
          <div className="bg-white border border-zinc-200 rounded-lg p-5 shadow-sm space-y-4">
            <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 block">PAYMENTS</span>
            <div className="flex items-baseline justify-between">
              <span className="text-xl font-bold text-zinc-950">
                ₹ {isPaid ? grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "0.00"}
              </span>
              <span className="text-xs text-zinc-400">
                {inv.dispatchDate ? new Date(inv.dispatchDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
              </span>
            </div>
            <div className="pt-2 border-t border-zinc-100 flex items-center justify-between text-xs">
              <span className="text-zinc-500">Method</span>
              <span className="font-medium text-zinc-800">
                {isPaid ? (inv.paymentMethod || "BANK_TRANSFER") : "—"}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-zinc-500">Status</span>
              <span className={`font-semibold ${isPaid ? "text-emerald-600" : "text-amber-600"}`}>
                {inv.status || "UNPAID"}
              </span>
            </div>

            {!isPaid && (
              <button
                onClick={() => setShowPaidModal(true)}
                disabled={markingPaid}
                className="w-full mt-2 py-2 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm transition disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                <CheckCircle className="w-3.5 h-3.5" />
                {markingPaid ? "Recording..." : "Record Payment (Mark Paid)"}
              </button>
            )}
          </div>
        </div>
      </div>{/* Mark Invoice as Paid Modal */}
      {showPaidModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 relative animate-in fade-in zoom-in-95 duration-150">
            <button
              onClick={() => setShowPaidModal(false)}
              className="absolute right-4 top-4 text-zinc-400 hover:text-zinc-600 transition-colors p-1"
            >
              <X className="w-5 h-5" />
            </button>

            <h3 className="text-lg font-bold text-zinc-950">Mark invoice as paid</h3>
            <p className="text-sm text-zinc-600 mt-2 font-medium">
              Amount: <span className="font-bold text-zinc-900">₹ {grandTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
            </p>

            <form onSubmit={handleMarkPaid} className="mt-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-zinc-700 mb-1.5">
                  Method
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-zinc-300 bg-white text-xs font-medium text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-zinc-900"
                >
                  <option value="BANK_TRANSFER">BANK_TRANSFER</option>
                  <option value="UPI">UPI</option>
                  <option value="CASH">CASH</option>
                  <option value="CHEQUE">CHEQUE</option>
                  <option value="OTHER">OTHER</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-zinc-700 mb-1.5">
                  Payment reference (optional)
                </label>
                <input
                  type="text"
                  placeholder="UTR / Txn ID"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-zinc-200 bg-zinc-50/50 text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:bg-white"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setShowPaidModal(false)}
                  className="px-4 py-2 rounded-lg border border-zinc-300 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={markingPaid}
                  className="px-4 py-2 rounded-lg bg-[#0f766e] hover:bg-[#115e59] text-white text-xs font-semibold shadow-sm transition-colors disabled:opacity-50"
                >
                  {markingPaid ? "Saving..." : "Mark paid"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}